import { inject, Injectable } from '@angular/core';
import { Observable, of, delay, BehaviorSubject } from 'rxjs';
import ApiOptionsConfig from './api-options.config';
import { CustomerService } from './customer.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { StoreService } from '../store.service';
import { environment } from '../../../environments/environment';
import { CategoriesService } from './general-settings/categories.service';

const BASE_URL = '/requests';
const BASE_URL_CATEGORIES = '/request-categories';
const BASE_URL_TRAFFIC_LIGHTS = '/request-traffic-lights';

@Injectable({
  providedIn: 'root'
})
export class RequestsService {


  accessToken: string = '';

  storeService: StoreService;
  httpClient: HttpClient;


  private customersSubject = new BehaviorSubject<string[]>([]);
  public customers$ = this.customersSubject.asObservable();
  private isCustomersLoaded = false;
  private categoriesSubject = new BehaviorSubject<string[]>([]);
  public categories$ = this.categoriesSubject.asObservable();
  private isCategoriesLoaded = false;

  private waitingTimeUsersMockData: any[] = [
    { userId: 1, name: 'Maurizio Soccodato', role: 'Administrator' },
    { userId: 2, name: 'Juan Daruich', role: 'Senior tester' },
    { userId: 3, name: 'Anna Maria Gaeta', role: 'Quality manager' },
    { userId: 4, name: 'Vincent Marmarà', role: 'Sales manager' },
    { userId: 5, name: 'Luca Ferrari', role: 'Project manager' },
  ];

  private waitingTimeSettingsMockObj: any = {
    green: {
      time: '01:00',
      notifications: [{ userId: 1, role: 'Administrator' }]
    },
    yellow: {
      time: '01:30',
      notifications: [
        { userId: 1, role: 'Administrator' },
        { userId: 2, role: 'Senior tester' }
      ]
    },
    red: {
      time: '02:00',
      notifications: [
        { userId: 1, role: 'Administrator' },
        { userId: 3, role: 'Quality manager' },
        { userId: 4, role: 'Sales manager' },
        { userId: 2, role: 'Senior tester' }
      ]
    }
  };



  constructor(private customerService: CustomerService) {

    this.storeService = inject(StoreService);
    this.httpClient = inject(HttpClient);

    this.storeService.getAuthToken().subscribe(
      (data: any) => { this.accessToken = data ? data['accessToken'] : null }
    )
  }


    loadCustomers(): Observable<any> {
    if (this.isCustomersLoaded) {
      return of(this.customersSubject.value);
    }

    const params = new HttpParams().set('size', '9999');
    this.customerService.list({ params }).subscribe({
      next: (response: any) => {
        const updatedCustomers = response.data.customers;
        this.customersSubject.next(updatedCustomers);
        this.isCustomersLoaded = true;
      },
      error: (error: any) => {
        console.error('Error fetching jurisdictions:', error);
      }
    });

    return this.customers$;
  }

  getCustomer(): Observable<any> {
    if (!this.isCustomersLoaded) {
      return this.loadCustomers();
    }
    return this.customers$;
  }

  GET(url: string, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.get<any>(url, {
      headers,
      params
    });
  }

  POST(url: string, data: any, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.post<any>(url, data, {
      headers,
      params
    });
  }


  PUT(url: string, data: any, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.put<any>(url, data, {
      headers,
      params
    });
  }

  DELETE(url: string, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.delete<any>(url, {
      headers,
      params
    });
  }
  getUrl(path: string): string {
    return `${environment.apiBaseUrl}${path}`
  }



  list(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL), options?.params);
  }

  detail(requestId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${requestId}`), options?.params);
  }

  create(data: any): Observable<any> {
    return this.POST(this.getUrl(BASE_URL), data);
  }

  update(requestId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${requestId}`), data);
  }

  delete(requestId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${requestId}`));
  }

  listSettingsRequestCategories(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_CATEGORIES), options?.params);
  }

  detailSettingsRequestCategories(requestCategoryId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_CATEGORIES}/${requestCategoryId}`), options?.params);
  }

  createSettingsRequestCategories(data: any): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_CATEGORIES), data);
  }

  updateSettingsRequestCategories(requestCategoryId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_CATEGORIES}/${requestCategoryId}`), data);
  }

  deleteSettingsRequestCategories(requestId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_CATEGORIES}/${requestId}`));
  }

  listNotes(requestId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${requestId}/notes`), options?.params);
  }

  createNotes(requestId: number, data: any): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${requestId}/notes`), data);
  }

  deleteNotes(requestId: number, sectionNoteId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${requestId}/notes/${sectionNoteId}`));
  }

  listWaitingTimeSettings(options?: ApiOptionsConfig): Observable<any> {
        return this.GET(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}`), options?.params);
  }

  listWaitingTimeUsers(requestTrafficLightId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}/users`), options?.params);
  }

  listWaitingTimeAvailableUsers(requestTrafficLightId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}/available-users`), options?.params);
  }

  createWaitingTimeUser(requestTrafficLightId: number, data: any, options?: ApiOptionsConfig): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}/users`), data, options?.params);
  }

  updateWaitingTimeUser(requestTrafficLightId: number, requestTrafficLightUserId: number, data: any, options?: ApiOptionsConfig): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}/users/${requestTrafficLightUserId}`), data, options?.params);
  }

  updateWaitingTimeSettings(requestTrafficLightId: number, data: any, options?: ApiOptionsConfig): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}`), data, options?.params);
  }

  deleteWaitingTimeUser(requestTrafficLightId: number, userId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_TRAFFIC_LIGHTS}/${requestTrafficLightId}/users/${userId}`), options?.params);
  }

  getCustomers(): any[] {
    return this.customersSubject.getValue();
  }


  listWaitingTimeUsersMock(): Observable<any[]> {
    return of([...this.waitingTimeUsersMockData]);
  }

  getWaitingTimeSettingsMock(): Observable<any> {
    return of(JSON.parse(JSON.stringify(this.waitingTimeSettingsMockObj)));
  }

  getWaitingTimeUserByIdMock(userId: string | number): any {
    return this.waitingTimeUsersMockData.find(u => String(u.userId) === String(userId)) || null;
  }

  saveWaitingTimeSettingsMock(data: any): Observable<any> {
    this.waitingTimeSettingsMockObj = data;
    return of({ success: true });
  }

}
